import { Stack } from "@volca/ui/components/ui/stack";
import { PAYROLL_RUN_STATUS } from "@/payroll/constants";
import { PackChatHeaderActions } from "@/payroll/pack-chat/pack-chat-section";
import { PayBreakdownHeader } from "@/payroll/pay-breakdown/pay-breakdown-header";
import { PayBreakdownSectionTabs } from "@/payroll/pay-breakdown/pay-breakdown-section-tabs";
import { PayrollRosterWarning } from "@/payroll/pay-breakdown/payroll-roster-warning";
import { PacksHeaderActions } from "@/payroll/pay-breakdown/payroll-statement-notifications/packs-section";
import { RequestApprovalAction } from "@/payroll/pay-breakdown/sheet-approval-requests";
import {
  PAY_BREAKDOWN_RUN_TARGET_TYPE,
  PAY_BREAKDOWN_SECTION,
} from "@/payroll/pay-breakdown/types";
import { PayrollNotesButton } from "@/payroll/period-notes";
import type { PayBreakdownPageState } from "./use-pay-breakdown-page";

interface PayBreakdownPageHeaderProps {
  page: PayBreakdownPageState;
}

const NO_OUTPUT = { type: PAY_BREAKDOWN_RUN_TARGET_TYPE.none } as const;

export const PayBreakdownPageHeader = ({
  page,
}: PayBreakdownPageHeaderProps) => {
  const {
    businessId,
    selectedPayPeriod,
    selectedPeriodStart,
    showLockPayPeriod,
    sheetApprovalActions,
    approvalStatus,
    output,
    aiScriptRunId,
    periods,
    calendarPeriods,
    isPeriodsLoading,
    selectedPeriod,
    openCommentCount,
    manualEditsToReview,
    canRequestApprovals,
    sheetApprovalState,
    canViewRoster,
    payrollEmailsAccess,
    canViewPayPlan,
    canViewPackChat,
    stAdjustmentsAccess,
    stAdjustmentsSectionActions,
    showSectionTabs,
    activeSection,
    setActiveSection,
    openPackThreadCount,
    isDownloadingAiWorkbook,
    rerunAction,
    setIsWorkbookExportOpen,
    handleOpenExplanationChat,
    handleOpenComments,
    handleOpenManualEdits,
    visibleRunAt,
    activeRun,
    periodReady,
    selectedRunStatus,
    hasGeneratedWorkbook,
    isLegacyDetailLoading,
    isPeriodDetailError,
    refetchPeriodDetail,
    calculationState,
    runReveal,
    canUseNotes,
    isNotesOpen,
    handleOpenNotes,
    noteComposer,
  } = page;
  const holding = runReveal.holding;

  return (
    <Stack align="stretch" className="w-full shrink-0" gap={4}>
      <PayBreakdownHeader
        selectedPeriod={selectedPeriod}
        visibleRunAt={holding ? null : visibleRunAt}
        rosterWarning={
          businessId && canViewRoster ? (
            <PayrollRosterWarning key={businessId} businessId={businessId} />
          ) : undefined
        }
        activeRun={activeRun}
        hasFailedRun={
          periodReady &&
          output.type !== PAY_BREAKDOWN_RUN_TARGET_TYPE.none &&
          selectedRunStatus === PAYROLL_RUN_STATUS.failed
        }
        approvalStatus={holding ? undefined : approvalStatus}
        approvalQueryState={
          hasGeneratedWorkbook && !holding
            ? {
                isLoading: isLegacyDetailLoading,
                isError: isPeriodDetailError,
                onRetry: () => {
                  void refetchPeriodDetail();
                },
              }
            : undefined
        }
        approvalActions={{
          pending: sheetApprovalActions.pendingApproval,
          onChange: sheetApprovalActions.handleChange,
        }}
        calculationState={calculationState}
        output={holding ? NO_OUTPUT : output}
        aiWorkbookDownload={{
          isDownloading: isDownloadingAiWorkbook,
          onDownload: () => setIsWorkbookExportOpen(true),
        }}
        onOpenExplanationChat={handleOpenExplanationChat}
        tabs={
          showSectionTabs && !holding ? (
            <PayBreakdownSectionTabs
              activeSection={activeSection}
              showRoster={canViewRoster}
              showPayPlan={canViewPayPlan}
              payrollEmailsAccess={payrollEmailsAccess}
              openThreadCount={openPackThreadCount}
              showPackChat={canViewPackChat}
              stAdjustmentsAccess={stAdjustmentsAccess}
              onSectionChange={setActiveSection}
            />
          ) : undefined
        }
        manualEdits={
          activeSection === PAY_BREAKDOWN_SECTION.breakdown
            ? {
                reviewCount: manualEditsToReview,
                onOpen: handleOpenManualEdits,
              }
            : undefined
        }
        comments={
          activeSection === PAY_BREAKDOWN_SECTION.breakdown && aiScriptRunId
            ? {
                openCount: openCommentCount,
                onOpen: handleOpenComments,
              }
            : undefined
        }
        rerunAction={rerunAction}
        nextPeriodNote={
          canUseNotes
            ? {
                onOpen: () => noteComposer.open(),
              }
            : undefined
        }
        approvalRequest={
          aiScriptRunId && canRequestApprovals ? (
            <RequestApprovalAction
              businessId={businessId}
              runId={aiScriptRunId}
              state={sheetApprovalState}
              approvalStatus={approvalStatus}
            />
          ) : undefined
        }
        periodPicker={{
          periods,
          calendarPeriods,
          selectedPeriodStart,
          isLoading: isPeriodsLoading,
        }}
        lock={
          showLockPayPeriod && selectedPayPeriod
            ? { businessId, period: selectedPayPeriod }
            : undefined
        }
        sectionActions={
          <>
            {canUseNotes && selectedPeriodStart && (
              <PayrollNotesButton
                businessId={businessId}
                periodStart={selectedPeriodStart}
                open={isNotesOpen}
                onOpen={handleOpenNotes}
              />
            )}
            {activeSection === PAY_BREAKDOWN_SECTION.packChat ? (
              <PackChatHeaderActions />
            ) : activeSection === PAY_BREAKDOWN_SECTION.packs ? (
              <PacksHeaderActions />
            ) : activeSection === PAY_BREAKDOWN_SECTION.adjustments ? (
              stAdjustmentsSectionActions
            ) : undefined}
          </>
        }
        hideDefaultActions={
          activeSection === PAY_BREAKDOWN_SECTION.packs ||
          activeSection === PAY_BREAKDOWN_SECTION.packChat ||
          activeSection === PAY_BREAKDOWN_SECTION.adjustments
        }
        showExplanationChatInMenu={
          activeSection === PAY_BREAKDOWN_SECTION.packChat
        }
      />
    </Stack>
  );
};
